import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../../utils/store';
import { useEffect, useState } from 'react';
import HttpService from '../../../services/HttpService';
import Constants from '../../../utils/constants';
import FormFieldData from '../../../Models/FormFieldData';
import { Stack } from '@mui/material';
import CommonForm from '../../../components/form/CommonForm';
import AlertMessageComponent from '../../../components/AlertMessageComponent';
import { useAlert } from '../../../components/alertContext';

const SWMSForm: React.FC = () => {
  const { getAdmin } = useStore();
  const { currentId } = useParams<{ currentId: any }>();
  const navigate = useNavigate();
  const { alert, setAlert, handleCloseAlert } = useAlert();
  const [currentRecord, setCurrentRecord] = useState<any>(null);
  type SWMSObj = {
    [k: string]: { required: boolean; value: string | null };
  };
  const [swmsForm, setSwmsForm] = useState<SWMSObj>({
    id: { required: false, value: null },
    method: { required: true, value: '' },
    hazards: { required: true, value: '' },
    risks: { required: true, value: '' },
    controlMeasures: { required: true, value: '' },
    revisedRiskRating: { required: true, value: '' },
    responsiblePerson: { required: true, value: '' },
  });
  const [loading, setLoading] = useState(false);

  const handleRowClick = (record: any) => {
    console.log(record);
    setCurrentRecord(record);
    setSwmsForm({
      id: { required: false, value: record.id },
      method: { required: true, value: record.method },
      hazards: { required: true, value: record.hazards },
      risks: { required: true, value: record.risks },
      controlMeasures: { required: true, value: record.controlMeasures },
      revisedRiskRating: { required: true, value: record.revisedRiskRating },
      responsiblePerson: { required: true, value: record.responsiblePerson },
    });
  };

  const fetchSingleRecord = async (id: string) => {
    try {
      const response = await HttpService.get(
        `${Constants.apiURL + Constants.swmsURL}/${id}`,
        { headers: { Authorization: `Bearer ${getAdmin()}` } },
        null
      );
      if (response.success) {
        handleRowClick(response.data);
      }
    } catch (error: any) {
      console.error(`GET Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    }
  };

  const clearForm = () => {
    setSwmsForm({
      id: { required: false, value: null },
      method: { required: true, value: '' },
      hazards: { required: true, value: '' },
      risks: { required: true, value: '' },
      controlMeasures: { required: true, value: '' },
      revisedRiskRating: { required: true, value: '' },
      responsiblePerson: { required: true, value: '' },
    });
    setAlert({
      type: 'error',
      message: '',
      open: false,
    });
    setCurrentRecord(null);
    navigate('/data-management/SWMS');
  };

  const handleSave = async () => {
    setLoading(true);
    const reqData: any = {
      method: swmsForm.method.value,
      hazards: swmsForm.hazards.value,
      risks: swmsForm.risks.value,
      controlMeasures: swmsForm.controlMeasures.value,
      revisedRiskRating: swmsForm.revisedRiskRating.value,
      responsiblePerson: swmsForm.responsiblePerson.value,
    };
    const endpoint = `${Constants.apiURL + Constants.swmsURL}`;
    let response: any;
    try {
      if (currentRecord) {
        reqData['id'] = swmsForm.id.value;
        response = await HttpService.put(endpoint, reqData, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      } else {
        response = await HttpService.post(endpoint, reqData, {
          headers: { Authorization: `Bearer ${getAdmin()}` },
        });
      }

      if (response.success) {
        clearForm();
      }
    } catch (error: any) {
      console.error(`POST Error:`, error);
      setAlert({
        type: 'error',
        message: error.message,
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSwmsForm((prevData) => ({
      ...prevData,
      [name]: { ...prevData[name], value: value },
    }));
  };

  useEffect(() => {
    if (currentId) {
      if (currentId !== 'Add') {
        fetchSingleRecord(currentId);
      }
    } else {
      setCurrentRecord(null);
    }
  }, [currentId]);

  const formFields: FormFieldData[] = [
    {
      name: 'method',
      label: 'Method',
      value: swmsForm.method.value,
      onChange: handleInputChange,
      isRequired: swmsForm.method.required,
      size: 6,
      type: 'textfield',
      validationRules: [(value) => (!value ? 'Method name is required' : null)],
    },
    {
      name: 'hazards',
      label: 'Hazards',
      value: swmsForm.hazards.value,
      onChange: handleInputChange,
      isRequired: swmsForm.hazards.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Hazard description is required' : null),
      ],
    },
    {
      name: 'risks',
      label: 'Risks',
      value: swmsForm.risks.value,
      onChange: handleInputChange,
      isRequired: swmsForm.risks.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Risk description is required' : null),
      ],
    },
    {
      name: 'controlMeasures',
      label: 'Control Measures',
      value: swmsForm.controlMeasures.value,
      onChange: handleInputChange,
      isRequired: swmsForm.controlMeasures.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Control measure is required' : null),
      ],
    },
    {
      name: 'revisedRiskRating',
      label: 'Revised Risk Rating',
      value: swmsForm.revisedRiskRating.value,
      onChange: handleInputChange,
      isRequired: swmsForm.revisedRiskRating.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Revised risk rating is required' : null),
      ],
    },
    {
      name: 'responsiblePerson',
      label: 'Responsible Person',
      value: swmsForm.responsiblePerson.value,
      onChange: handleInputChange,
      isRequired: swmsForm.responsiblePerson.required,
      size: 6,
      type: 'textfield',
      validationRules: [
        (value) => (!value ? 'Responsible person is required' : null),
      ],
    },
  ];
  return (
    <Stack>
      <CommonForm
        fields={formFields}
        onCancel={() => {}}
        formReset={clearForm}
        formTitle={currentId !== 'Add' ? 'Update SWMS' : 'Add New SWMS'}
        isLoading={loading}
        isUpdate={currentId !== 'Add' ? true : false}
        onSave={handleSave}
        actionButtonPosition="bottom"
      />
      <AlertMessageComponent
        type={alert.type}
        open={alert.open}
        handleClose={handleCloseAlert}
        message={alert.message}
      />
    </Stack>
  );
};

export default SWMSForm;
